import React from 'react';
import Button from '@amzn/awsui-components-core/button';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import Container from '@amzn/awsui-components-core/container';
import FormField from '@amzn/awsui-components-core/form-field';
import Header from '@amzn/awsui-components-core/header';
import Input from '@amzn/awsui-components-core/input';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import type { MeasureFormat } from 'src/components/star-grid/datasets/datasetTypes';

const FORMAT_TEXT: Record<MeasureFormat, Parameters<StarGridText>[0]> = {
  currency: 'wizardMeasureFormatCurrency',
  number: 'wizardMeasureFormatNumber',
  percentage: 'wizardMeasureFormatPercent'
};

/** Measures defined as formulas over the table's measures; the service checks each formula on preview and save. */
export const WizardCalculatedMeasures: React.FC<{ state: DatasetWizardState; text: StarGridText }> = ({ state, text }) => {
  const formats = (Object.keys(FORMAT_TEXT) as MeasureFormat[]).map((format) => ({ value: format, label: text(FORMAT_TEXT[format]) }));
  return (
    <Container
      header={
        <Header
          variant="h3"
          description={text('wizardCalculatedMeasuresHelp')}
          actions={
            <Button iconName="add-plus" onClick={state.addCalculatedMeasure} data-testid="wizard-calculated-add">
              {text('wizardCalculatedAdd')}
            </Button>
          }
        >
          {text('wizardCalculatedMeasures')}
        </Header>
      }
      data-testid="wizard-calculated"
    >
      <SpaceBetween size="m">
        {state.calculatedMeasures.map((measure, index) => (
          <ColumnLayout key={index} columns={4}>
            <FormField label={text('wizardCalculatedName')}>
              <Input value={measure.name} onChange={({ detail }) => state.updateCalculatedMeasure(index, { ...measure, name: detail.value })} />
            </FormField>
            <FormField label={text('wizardCalculatedFormula')} description={text('wizardCalculatedFormulaHelp')}>
              <Input
                value={measure.formula}
                placeholder="=[gp] / [revenue]"
                data-testid={`wizard-calculated-formula-${index}`}
                onChange={({ detail }) => state.updateCalculatedMeasure(index, { ...measure, formula: detail.value })}
              />
            </FormField>
            <FormField label={text('wizardCalculatedFormat')}>
              <Select
                options={formats}
                selectedOption={formats.find((option) => option.value === measure.format) ?? formats[0]}
                onChange={({ detail }) => state.updateCalculatedMeasure(index, { ...measure, format: detail.selectedOption.value as MeasureFormat })}
              />
            </FormField>
            <SpaceBetween size="xs">
              <Checkbox
                checked={measure.per_row}
                onChange={({ detail }) => state.updateCalculatedMeasure(index, { ...measure, per_row: detail.checked })}
              >
                {text('wizardCalculatedPerRow')}
              </Checkbox>
              <Button variant="inline-link" onClick={() => state.removeCalculatedMeasure(index)}>
                {text('wizardCalculatedRemove')}
              </Button>
            </SpaceBetween>
          </ColumnLayout>
        ))}
      </SpaceBetween>
    </Container>
  );
};
